import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useSimulationData } from "../../../../hooks/useSimulationData";
import { LOOSE, readColumn } from "../../utils/columnLookup";
import { parseSimulationDate } from "../../utils/simulationDate";
import { buildDataZoom, darkTooltip } from "../../utils/chartOptions";

// Lookup keys mirror TransferChart.jsx so the verification chart agrees with
// the rest of the app. The "A-B" form is a difference expression handled below.
const TRANSFER_KEYS = [
  "Totals Total Transfer (kWh)",
  "Energy Buy/Sell (kWh)",
  "Utility Grid Import (kWh)-Utility Grid Export (kWh)",
];

// Resolve a transfer value from a raw timeseries row using the same fallback
// chain as TransferChart, through the same lookup module, so the verification
// chart and the Transfer dashboard can no longer resolve different columns for
// the same row. Returns the raw column value (positive = export per CSV
// convention); callers flip the sign to match the chart's display.
//
// Keeps the loose fallback its inline version applied unconditionally, because
// the chain above carries bare legacy names; it now runs only after every exact
// key has missed. See utils/columnLookup.js.
export const getTransferValue = (entry) =>
  readColumn(entry, TRANSFER_KEYS, { fallback: LOOSE });

// Bucket rows by YYYY-MM-DD and emit sorted per-day aggregates of the signed
// transfer value (sign flipped to match the Transfer chart: positive = import).
export const aggregateByDay = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const byDay = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    const entry = rows[i];
    /* Parsed rather than sliced: "01/01/2040 00:00".slice(0, 10) is
     * "01/01/2040", which passes a length check but then sorts and
     * range-filters lexicographically against the ISO values the
     * <input type="date"> controls below produce. parseSimulationDate returns
     * an ISO day for either stamp shape, and null - a skipped row - for one it
     * cannot read, which is what the old length check was reaching for. */
    const day = parseSimulationDate(entry && entry.Date)?.day;
    if (!day) continue;

    const value = -getTransferValue(entry);
    const bucket = byDay.get(day);
    if (bucket) {
      bucket.sum += value;
      bucket.count += 1;
      if (value < bucket.min) bucket.min = value;
      if (value > bucket.max) bucket.max = value;
    } else {
      byDay.set(day, { sum: value, count: 1, min: value, max: value });
    }
  }

  const days = Array.from(byDay.keys()).sort();
  return days.map((day) => {
    const { sum, count, min, max } = byDay.get(day);
    return {
      day,
      min,
      max,
      avg: count > 0 ? sum / count : 0,
      count,
    };
  });
};

const PURPLE_SOLID = "rgba(103, 90, 255, 1)";
const PURPLE_FILL = "rgba(103, 90, 255, 0.18)";

const formatTooltipValue = (n) => {
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

// Inclusive YYYY-MM-DD comparison helpers (lexicographic order works for ISO
// date strings).
const clampDay = (day, lo, hi) => {
  if (!day) return day;
  if (lo && day < lo) return lo;
  if (hi && day > hi) return hi;
  return day;
};

// Subtract `days - 1` from a YYYY-MM-DD string and clamp to lower bound.
const subtractDays = (anchorDay, count, lowerBound) => {
  if (!anchorDay) return anchorDay;
  const d = new Date(`${anchorDay}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return anchorDay;
  d.setUTCDate(d.getUTCDate() - (count - 1));
  const candidate = d.toISOString().slice(0, 10);
  return lowerBound && candidate < lowerBound ? lowerBound : candidate;
};

export default function TransferDailyRangeChart() {
  const { dailyData, isLoading, error } = useSimulationData();

  const agg = useMemo(() => aggregateByDay(dailyData || []), [dailyData]);

  const dataMinDay = agg.length > 0 ? agg[0].day : "";
  const dataMaxDay = agg.length > 0 ? agg[agg.length - 1].day : "";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Reset the date range whenever the underlying data bounds change (new
  // simulation loaded, etc.) or if the previously-selected range now falls
  // outside the available data.
  useEffect(() => {
    if (!dataMinDay || !dataMaxDay) {
      if (startDate !== "" || endDate !== "") {
        setStartDate("");
        setEndDate("");
      }
      return;
    }
    const inRange =
      startDate &&
      endDate &&
      startDate >= dataMinDay &&
      endDate <= dataMaxDay &&
      startDate <= endDate;
    if (!inRange) {
      setStartDate(dataMinDay);
      setEndDate(dataMaxDay);
    }
    // We intentionally only react to changes in the data bounds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMinDay, dataMaxDay]);

  const filteredAgg = useMemo(() => {
    if (agg.length === 0) return agg;
    if (!startDate || !endDate) return agg;
    return agg.filter((d) => d.day >= startDate && d.day <= endDate);
  }, [agg, startDate, endDate]);

  const handleStartChange = (e) => {
    const next = clampDay(e.target.value, dataMinDay, dataMaxDay);
    setStartDate(next);
    if (endDate && next > endDate) setEndDate(next);
  };

  const handleEndChange = (e) => {
    const next = clampDay(e.target.value, dataMinDay, dataMaxDay);
    setEndDate(next);
    if (startDate && next < startDate) setStartDate(next);
  };

  const handleReset = () => {
    setStartDate(dataMinDay);
    setEndDate(dataMaxDay);
  };

  const handlePreset = (days) => {
    if (!dataMaxDay) return;
    setStartDate(subtractDays(dataMaxDay, days, dataMinDay));
    setEndDate(dataMaxDay);
  };

  const isFullRange =
    startDate === dataMinDay && endDate === dataMaxDay && agg.length > 0;

  const option = useMemo(() => {
    const days = filteredAgg.map((d) => d.day);
    const mins = filteredAgg.map((d) => d.min);
    const ranges = filteredAgg.map((d) => d.max - d.min);
    const avgs = filteredAgg.map((d) => d.avg);

    return {
      tooltip: darkTooltip({
        fontSize: 12,
        borderColor: "white",
        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const idx = params[0].dataIndex;
          const row = filteredAgg[idx];
          if (!row) return "";
          return [
            `<div style="font-weight:600;margin-bottom:4px;">${row.day}</div>`,
            `Max: ${formatTooltipValue(row.max)} kWh`,
            `Avg: ${formatTooltipValue(row.avg)} kWh`,
            `Min: ${formatTooltipValue(row.min)} kWh`,
          ].join("<br/>");
        },
      }),
      legend: {
        data: ["Daily average"],
        top: 0,
      },
      grid: {
        left: "40px",
        right: "20px",
        top: "30px",
        bottom: "70px",
        containLabel: true,
      },
      dataZoom: buildDataZoom({ accent: PURPLE_SOLID, bottom: 10 }),
      xAxis: {
        type: "category",
        data: days,
        name: "Day",
        nameLocation: "middle",
        nameGap: 30,
        axisLine: { lineStyle: { color: "#999" } },
        axisLabel: { color: "#666" },
      },
      yAxis: {
        type: "value",
        name: "Transfer (kWh)",
        nameLocation: "middle",
        nameGap: 50,
        axisLine: { lineStyle: { color: "#999" } },
        axisLabel: { color: "#666" },
        splitLine: { lineStyle: { color: "#eee" } },
      },
      series: [
        {
          name: "Min",
          type: "line",
          data: mins,
          stack: "band",
          stackStrategy: "all",
          symbol: "none",
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          tooltip: { show: false },
          legendHoverLink: false,
          silent: true,
        },
        {
          name: "Range",
          type: "line",
          data: ranges,
          stack: "band",
          stackStrategy: "all",
          symbol: "none",
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          areaStyle: { color: PURPLE_FILL },
          tooltip: { show: false },
          legendHoverLink: false,
          silent: true,
        },
        {
          name: "Daily average",
          type: "line",
          data: avgs,
          smooth: true,
          symbol: "circle",
          symbolSize: 5,
          lineStyle: { width: 2, color: PURPLE_SOLID },
          itemStyle: { color: PURPLE_SOLID },
        },
      ],
    };
  }, [filteredAgg]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <p className="verification-raw-message">Loading transfer data...</p>
      );
    }
    if (error) {
      return (
        <p className="verification-raw-message verification-raw-message--error">
          {error}
        </p>
      );
    }
    if (!dailyData || dailyData.length === 0 || agg.length === 0) {
      return (
        <p className="verification-raw-message">
          No transfer data available for the selected simulation.
        </p>
      );
    }
    if (filteredAgg.length === 0) {
      return (
        <p
          className="verification-raw-message"
          data-testid="transfer-range-empty"
        >
          No days fall within the selected range.
        </p>
      );
    }
    return (
      <div
        className="verification-chart-wrapper"
        data-testid="transfer-range-chart"
      >
        <ReactECharts
          option={option}
          style={{ width: "100%", height: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    );
  };

  const renderControls = () => {
    if (agg.length === 0) return null;
    return (
      <div
        className="verification-chart-controls"
        data-testid="transfer-range-controls"
      >
        <div className="verification-chart-controls-row">
          <label className="verification-chart-control">
            <span className="verification-chart-control-label">From</span>
            <input
              type="date"
              value={startDate}
              min={dataMinDay}
              max={dataMaxDay}
              onChange={handleStartChange}
              data-testid="transfer-range-start"
            />
          </label>
          <label className="verification-chart-control">
            <span className="verification-chart-control-label">To</span>
            <input
              type="date"
              value={endDate}
              min={dataMinDay}
              max={dataMaxDay}
              onChange={handleEndChange}
              data-testid="transfer-range-end"
            />
          </label>
          <div
            className="verification-chart-presets"
            role="group"
            aria-label="Quick date range presets"
          >
            <button
              type="button"
              className="verification-chart-preset"
              onClick={() => handlePreset(7)}
            >
              7d
            </button>
            <button
              type="button"
              className="verification-chart-preset"
              onClick={() => handlePreset(30)}
            >
              30d
            </button>
            <button
              type="button"
              className="verification-chart-preset"
              onClick={() => handlePreset(90)}
            >
              90d
            </button>
            <button
              type="button"
              className="verification-chart-preset"
              onClick={handleReset}
              disabled={isFullRange}
              data-testid="transfer-range-reset"
            >
              All
            </button>
          </div>
        </div>
        <p className="verification-chart-controls-caption">
          Showing {filteredAgg.length} day{filteredAgg.length === 1 ? "" : "s"}{" "}
          of {agg.length} available
          {dataMinDay && dataMaxDay
            ? ` (${dataMinDay} \u2013 ${dataMaxDay})`
            : ""}
          .
        </p>
      </div>
    );
  };

  return (
    <section
      className="verification-section verification-chart-section"
      aria-label="Daily transfer range chart"
    >
      <div className="verification-section-header">
        <h2 className="verification-section-title">Daily transfer range</h2>
      </div>
      <p className="verification-section-note">
        Per-day minimum, average, and maximum of the signed grid transfer
        value. Positive = grid import; negative = grid export.
      </p>
      {renderControls()}
      {renderBody()}
    </section>
  );
}
